import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Users, Plus, Play, Pause, RotateCcw, Timer, Video, Search,
  Loader2, Clock, ArrowRight, Coffee, Brain, Flame, Copy, X, SkipForward, Maximize2, Minimize2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Tip from '../components/Tip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTimer = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
};

const StudyRoomsPage = () => {
  const [tab, setTab] = useState('browse');
  const [rooms, setRooms] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', subject: '', description: '', pomodoro_work: 25, pomodoro_break: 5 });
  const [creating, setCreating] = useState(false);
  const [activeRoom, setActiveRoom] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [videoFullscreen, setVideoFullscreen] = useState(false);
  const [videoMinimized, setVideoMinimized] = useState(false);
  const [videoDragging, setVideoDragging] = useState(false);
  const [timerDisplay, setTimerDisplay] = useState(0);
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?subject=${encodeURIComponent(search)}` : '';
      const [browse, mine] = await Promise.all([
        axios.get(`${API}/study-rooms/browse${params}`),
        axios.get(`${API}/study-rooms/my-rooms`),
      ]);
      setRooms(browse.data.rooms || []);
      setMyRooms(mine.data.rooms || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const handleCreate = async () => {
    if (!createForm.name.trim()) return toast.error('Give your room a name');
    setCreating(true);
    try {
      const res = await axios.post(`${API}/study-rooms/create`, createForm);
      toast.success('Study room created!');
      setShowCreate(false);
      await fetchRooms();
      handleJoin(res.data.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setCreating(false);
  };

  const handleJoin = async (roomId) => {
    try {
      const res = await axios.post(`${API}/study-rooms/join/${roomId}`);
      setActiveRoom(res.data);
      setTimerDisplay(res.data.timer?.remaining_seconds || 0);
      startPolling(roomId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
  };

  const startPolling = (roomId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/study-rooms/room/${roomId}`);
        setActiveRoom(res.data);
      } catch (err) { console.warn('Failed to fetch rooms:', err.message); }
    }, 5000);
  };

  const handleTimerAction = async (action) => {
    if (!activeRoom) return;
    try {
      const res = await axios.post(`${API}/study-rooms/timer/${activeRoom.id}`, { action });
      setActiveRoom(prev => ({ ...prev, timer: res.data.timer }));
      setTimerDisplay(res.data.timer.remaining_seconds);
    } catch (err) { toast.error('Timer action failed'); }
  };

  const handleLeave = async () => {
    if (!activeRoom) return;
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    try { await axios.post(`${API}/study-rooms/leave/${activeRoom.id}`); } catch (err) { console.warn('Leave room error:', err.message); }
    setActiveRoom(null);
    setShowVideo(false);
    fetchRooms();
  };

  const copyShareLink = () => {
    if (!activeRoom) return;
    navigator.clipboard.writeText(`${window.location.origin}/study-rooms`);
    toast.success('Room link copied!');
  };

  // Local timer countdown
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (activeRoom?.timer?.state === 'work' || activeRoom?.timer?.state === 'break') {
      setTimerDisplay(activeRoom.timer.remaining_seconds);
      timerRef.current = setInterval(() => {
        setTimerDisplay(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            const nextAction = activeRoom.timer.state === 'work' ? 'complete_work' : 'complete_break';
            handleTimerAction(nextAction);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // Only restart timer when state or started_at changes; remaining_seconds
    // and handleTimerAction are intentionally excluded to avoid resetting on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoom?.timer?.state, activeRoom?.timer?.started_at]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Active room view
  if (activeRoom) {
    const timer = activeRoom.timer || {};
    const config = activeRoom.pomodoro_config || {};
    const isWork = timer.state === 'work';
    const isBreak = timer.state === 'break';
    const isPaused = timer.state === 'paused';
    const isIdle = timer.state === 'idle';

    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="study-room-active">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[var(--isp-heading)]">{activeRoom.name}</h1>
            <p className="text-sm text-[var(--isp-body)]">{activeRoom.subject || 'General Study'}</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={copyShareLink} className="rounded-lg gap-1 border-[var(--isp-glass-border)] text-[var(--isp-body)]" data-testid="copy-room-link">
              <Copy className="w-3.5 h-3.5" /> Share
            </Button>
            <Button size="sm" variant="outline" onClick={handleLeave} className="rounded-lg gap-1 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="leave-room-btn">
              <X className="w-3.5 h-3.5" /> Leave
            </Button>
          </div>
        </div>

        {/* Timer Card */}
        <motion.div
          className={`isp-card p-8 text-center ${isWork ? 'border-[var(--isp-accent)]/30' : isBreak ? 'border-[var(--isp-amber)]/30' : ''}`}
          data-testid="pomodoro-timer"
        >
          <div className="mb-2">
            {isWork && <span className="isp-tag bg-[var(--isp-accent)]/15 text-[var(--isp-accent)] border-[var(--isp-accent)]/30"><Brain className="w-3 h-3" /> Focus Time</span>}
            {isBreak && <span className="isp-tag bg-[var(--isp-amber)]/15 text-[var(--isp-amber)] border-[var(--isp-amber)]/30"><Coffee className="w-3 h-3" /> Break</span>}
            {isPaused && <span className="isp-tag bg-white/10 text-[var(--isp-body)]"><Pause className="w-3 h-3" /> Paused</span>}
            {isIdle && <span className="isp-tag bg-white/10 text-[var(--isp-body)]"><Timer className="w-3 h-3" /> Ready</span>}
          </div>

          <div className={`text-7xl font-mono font-bold tracking-wider my-6 ${isWork ? 'text-[var(--isp-accent)]' : isBreak ? 'text-[var(--isp-amber)]' : 'text-[var(--isp-heading)]'}`}>
            {formatTimer(timerDisplay || (isIdle ? config.work_minutes * 60 : 0))}
          </div>

          <p className="text-xs text-[var(--isp-muted)] mb-6">
            Cycle {timer.cycle || 0} · {config.work_minutes}min work / {config.break_minutes}min break
          </p>

          <div className="flex gap-3 justify-center">
            {(isIdle || isPaused) && (
              <Button onClick={() => handleTimerAction('start')} className="isp-btn-primary rounded-full px-8 gap-2" data-testid="timer-start">
                <Play className="w-4 h-4" /> {isPaused ? 'Resume' : 'Start Focus'}
              </Button>
            )}
            {(isWork || isBreak) && (
              <Button onClick={() => handleTimerAction('pause')} className="isp-btn-secondary rounded-full px-8 gap-2" data-testid="timer-pause">
                <Pause className="w-4 h-4" /> Pause
              </Button>
            )}
            {isBreak && (
              <Button onClick={() => handleTimerAction('skip_break')} className="isp-btn-coral rounded-full px-6 gap-2" data-testid="timer-skip">
                <SkipForward className="w-4 h-4" /> Skip Break
              </Button>
            )}
            <Button onClick={() => handleTimerAction('reset')} variant="outline" className="rounded-full px-6 gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)]" data-testid="timer-reset">
              <RotateCcw className="w-4 h-4" /> Reset
            </Button>
          </div>
        </motion.div>

        {/* Video Section — Full Width when active */}
        {showVideo && !videoMinimized && !videoFullscreen && (
          <div className="isp-card overflow-hidden" data-testid="room-video-active">
            <div className="relative">
              <iframe
                src={`https://${activeRoom.jitsi_domain}/${activeRoom.room_code}#config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false`}
                allow="camera; microphone; display-capture; fullscreen"
                className="w-full aspect-video sm:aspect-[21/9] border-0"
                title="Study Room Video"
                data-testid="jitsi-iframe"
              />
              <div className="absolute top-3 right-3 flex gap-1.5">
                <button onClick={() => setVideoFullscreen(true)} className="p-2 bg-black/60 backdrop-blur-sm rounded-lg text-white hover:bg-black/80 transition-all" title="Fullscreen">
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button onClick={() => setVideoMinimized(true)} className="p-2 bg-black/60 backdrop-blur-sm rounded-lg text-white hover:bg-black/80 transition-all" title="Picture-in-picture">
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button onClick={() => setShowVideo(false)} className="p-2 bg-red-500/80 backdrop-blur-sm rounded-lg text-white hover:bg-red-600 transition-all" title="Leave video">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg text-white text-xs font-medium flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                {activeRoom.participant_count} in video · {activeRoom.name}
              </div>
            </div>
          </div>
        )}

        {/* Minimized PIP */}
        {showVideo && videoMinimized && (
          <div className="fixed bottom-24 right-6 z-50 w-72 h-44  overflow-hidden shadow-2xl border border-[var(--isp-glass-border)] bg-black" data-testid="video-pip">
            <iframe
              src={`https://${activeRoom.jitsi_domain}/${activeRoom.room_code}#config.prejoinPageEnabled=false`}
              allow="camera; microphone; display-capture"
              className="w-full h-full"
              title="Study Room Video"
            />
            <div className="absolute top-2 right-2 flex gap-1">
              <button onClick={() => setVideoMinimized(false)} className="p-1.5 bg-black/70 rounded-lg text-white hover:bg-black/90" title="Expand">
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => { setVideoMinimized(false); setShowVideo(false); }} className="p-1.5 bg-red-500/80 rounded-lg text-white hover:bg-red-600" title="Leave">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Fullscreen */}
        {showVideo && videoFullscreen && (
          <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="video-fullscreen">
            <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-b from-black/80 to-transparent absolute top-0 left-0 right-0 z-10">
              <span className="text-white text-sm font-medium">{activeRoom.name}</span>
              <div className="flex gap-2">
                <button onClick={() => setVideoFullscreen(false)} className="p-2 bg-white/10 rounded-lg text-white hover:bg-white/20" title="Exit fullscreen">
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button onClick={() => { setVideoMinimized(true); setVideoFullscreen(false); }} className="p-2 bg-white/10 rounded-lg text-white hover:bg-white/20" title="PIP mode">
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button onClick={() => { setVideoFullscreen(false); setShowVideo(false); }} className="p-2 bg-red-500 rounded-lg text-white hover:bg-red-600" title="Leave">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <iframe
              src={`https://${activeRoom.jitsi_domain}/${activeRoom.room_code}#config.prejoinPageEnabled=false`}
              allow="camera; microphone; display-capture; fullscreen"
              className="flex-1 w-full"
              title="Study Room Video"
            />
          </div>
        )}

        {/* Participants + Join Video */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Participants */}
          <div className="isp-card p-5" data-testid="room-participants">
            <h3 className="text-sm font-semibold text-[var(--isp-heading)] mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-[var(--isp-accent)]" />
              {activeRoom.participant_count} in room
            </h3>
            <div className="space-y-2">
              {(activeRoom.participants || []).map((p, i) => (
                <div key={p.user_id || i} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--isp-glass-inner)]">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[var(--isp-accent)] to-emerald-600 flex items-center justify-center text-black text-xs font-bold">
                    {(p.name || 'U')[0].toUpperCase()}
                  </div>
                  <span className="text-sm text-[var(--isp-heading)] flex-1">{p.name || 'Student'}</span>
                  <span className="text-xs text-[var(--isp-accent)] flex items-center gap-1"><Flame className="w-3 h-3" /> {p.pomodoros_completed}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Video Join Card (only when video is off) */}
          {!showVideo && (
            <div className="isp-card p-5" data-testid="room-video">
              <h3 className="text-sm font-semibold text-[var(--isp-heading)] mb-3 flex items-center gap-2">
                <Video className="w-4 h-4 text-[#002FA7]" /> Video Study
              </h3>
              <div className="text-center py-4">
                <Video className="w-12 h-12 mx-auto text-[var(--isp-muted)] mb-3" />
                <p className="text-sm text-[var(--isp-heading)] font-medium mb-1">Study together on video</p>
                <p className="text-xs text-[var(--isp-muted)] mb-4">Join a live video session with everyone in this room</p>
                <Button onClick={() => setShowVideo(true)} className="rounded-full gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white px-6" data-testid="join-video-btn">
                  <Video className="w-4 h-4" /> Join Video Call
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-center gap-6 text-center">
          <div>
            <p className="text-2xl font-bold text-[var(--isp-accent)]">{activeRoom.total_pomodoros}</p>
            <p className="text-[10px] text-[var(--isp-muted)]">Total Pomodoros</p>
          </div>
          <div className="w-px h-8 bg-[var(--isp-divider)]" />
          <div>
            <p className="text-2xl font-bold text-[var(--isp-heading)]">{activeRoom.participant_count}</p>
            <p className="text-[10px] text-[var(--isp-muted)]">Students</p>
          </div>
          <div className="w-px h-8 bg-[var(--isp-divider)]" />
          <div>
            <p className="text-2xl font-bold text-[var(--isp-amber)]">{(activeRoom.total_pomodoros || 0) * (config.work_minutes || 25)}</p>
            <p className="text-[10px] text-[var(--isp-muted)]">Focus Minutes</p>
          </div>
        </div>
      </div>
    );
  }

  // Browse/Create view
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="study-rooms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[var(--isp-accent)] to-emerald-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-black" />
            </div>
            Study Rooms
          </h1>
          <p className="text-[var(--isp-body)] mt-1">Study together with Pomodoro focus timers</p>
        </div>
        <Tip text="Create a Pomodoro study room and invite friends">
          <Button onClick={() => setShowCreate(true)} className="isp-btn-primary rounded-full gap-2" data-testid="create-room-btn">
            <Plus className="w-4 h-4" /> Create Room
          </Button>
        </Tip>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[{ id: 'browse', label: 'Public Rooms' }, { id: 'my', label: 'My Rooms' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-[var(--isp-accent)]/20 text-[var(--isp-accent)] border border-[var(--isp-accent)]/30' : 'text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]'}`}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--isp-muted)]" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by subject..."
          className="pl-9 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] rounded-xl" data-testid="room-search" />
      </div>

      {/* Room List */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[var(--isp-accent)]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rooms-grid">
          {(tab === 'browse' ? rooms : myRooms).length === 0 ? (
            <div className="col-span-2 isp-card p-10 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 text-[var(--isp-muted)]" />
              <p className="text-[var(--isp-body)]">{tab === 'browse' ? 'No public rooms active right now' : 'You haven\'t joined any rooms'}</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4 isp-btn-primary rounded-full gap-2">
                <Plus className="w-4 h-4" /> Create One
              </Button>
            </div>
          ) : (tab === 'browse' ? rooms : myRooms).map((room, i) => (
            <motion.div key={room.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <div className="isp-card p-5 cursor-pointer hover:border-[var(--isp-accent)]/30 transition-all" onClick={() => handleJoin(room.id)} data-testid={`room-${room.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--isp-heading)]">{room.name}</h3>
                    <p className="text-[10px] text-[var(--isp-muted)]">{room.subject || 'General'} · by {room.host_name}</p>
                  </div>
                  <span className={`isp-tag ${room.timer?.state === 'work' ? 'bg-[var(--isp-accent)]/15 text-[var(--isp-accent)]' : room.timer?.state === 'break' ? 'bg-[var(--isp-amber)]/15 text-[var(--isp-amber)]' : 'bg-white/10 text-[var(--isp-muted)]'}`}>
                    {room.timer?.state === 'work' ? 'Focusing' : room.timer?.state === 'break' ? 'Break' : 'Idle'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-[10px] text-[var(--isp-muted)]">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {room.participant_count}/{room.max_participants}</span>
                  <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {room.total_pomodoros} pomodoros</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {room.pomodoro_config?.work_minutes}m / {room.pomodoro_config?.break_minutes}m</span>
                </div>
                {(room.participants || []).length > 0 && (
                  <div className="flex -space-x-2 mt-3">
                    {room.participants.slice(0, 5).map((p, pi) => (
                      <div key={`room-${room.id}-p-${p.user_id || p.id || pi}`} className="w-6 h-6 rounded-full bg-gradient-to-br from-[var(--isp-accent)] to-emerald-600 border-2 border-[var(--isp-glass)] flex items-center justify-center text-black text-[8px] font-bold">
                        {(p.name || 'U')[0].toUpperCase()}
                      </div>
                    ))}
                    {room.participant_count > 5 && <span className="text-[10px] text-[var(--isp-muted)] ml-2">+{room.participant_count - 5}</span>}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Room Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]" data-testid="create-room-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-[var(--isp-accent)]" /> Create Study Room</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder="Room name (e.g. 'Bio Exam Prep')"
              className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="room-name-input" />
            <Input value={createForm.subject} onChange={e => setCreateForm(p => ({ ...p, subject: e.target.value }))} placeholder="Subject (optional)"
              className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="room-subject-input" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--isp-body)] mb-1 block">Work (min)</label>
                <Input type="number" value={createForm.pomodoro_work} onChange={e => setCreateForm(p => ({ ...p, pomodoro_work: parseInt(e.target.value) || 25 }))}
                  min={5} max={60} className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="room-work-input" />
              </div>
              <div>
                <label className="text-xs text-[var(--isp-body)] mb-1 block">Break (min)</label>
                <Input type="number" value={createForm.pomodoro_break} onChange={e => setCreateForm(p => ({ ...p, pomodoro_break: parseInt(e.target.value) || 5 }))}
                  min={1} max={30} className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="room-break-input" />
              </div>
            </div>
            <Button onClick={handleCreate} disabled={creating} className="w-full isp-btn-primary rounded-full gap-2" data-testid="create-room-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Create & Join</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudyRoomsPage;
